import { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import api, { API_BASE, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const FIELDS = [
  { key: "site_name", label: "Site name", type: "text" },
  { key: "logo_url", label: "Logo URL", type: "text" },
  { key: "cashtag", label: "Cash App Cashtag", type: "text" },
  { key: "credits_per_surf", label: "Credits per surf", type: "number", step: 0.1 },
  { key: "cash_per_1000_surfs", label: "Cash per 1000 surfs ($)", type: "number", step: 0.01 },
  { key: "surf_timer_seconds", label: "Default surf timer (s) — free users", type: "number" },
  { key: "verify_every_n_surfs", label: "Verify every N surfs (1 = every)", type: "number" },
  { key: "min_surf_time_seconds", label: "Min surf time (s)", type: "number" },
  { key: "max_websites_per_user", label: "Max websites per user", type: "number" },
  { key: "referral_percent", label: "Referral %", type: "number" },
  { key: "welcome_credits", label: "Welcome credits", type: "number" },
  { key: "welcome_xp", label: "Welcome XP", type: "number" },
  { key: "footer_text", label: "Footer text", type: "text" },
  { key: "homepage_headline", label: "Homepage headline", type: "text" },
  { key: "homepage_subheadline", label: "Homepage subheadline", type: "text" },
];

const SEO_FIELDS = [
  { key: "meta_title", label: "Browser tab title", type: "text", hint: "Shown in the browser tab and search results" },
  { key: "meta_description", label: "Meta description", type: "textarea", hint: "155-160 chars for best SEO" },
  { key: "meta_keywords", label: "Meta keywords", type: "text", hint: "Comma-separated keywords" },
];

const TOGGLES = [
  { key: "registration_open", label: "Registration open" },
  { key: "email_verification_required", label: "Require email verification" },
  { key: "anti_cheat_enabled", label: "Anti-cheat enabled" },
  { key: "maintenance_mode", label: "Maintenance mode" },
  { key: "google_signin_enabled", label: "Enable Google Sign-In" },
  { key: "start_pages_enabled", label: "Enable Start Pages feature" },
];

const GOOGLE_FIELDS = [
  { key: "google_client_id", label: "Google OAuth Client ID", type: "text", hint: "Get this from Google Cloud Console → APIs & Services → Credentials" },
  { key: "google_client_secret", label: "Google OAuth Client Secret", type: "password", hint: "Kept server-side only — never exposed to browsers" },
];

const START_PAGE_FIELDS = [
  { key: "start_pages_price_usd", label: "Price per Start Page pack (USD)", type: "number", step: 0.01 },
  { key: "start_pages_pack_size", label: "Views per pack", type: "number" },
  { key: "start_pages_daily_price_usd", label: "Price per reserved day (USD)", type: "number", step: 0.01 },
  { key: "start_pages_checkout_url", label: "External checkout URL for date reservations", type: "text", hint: "Leave blank to fall back to the user's cash wallet. Supports {username}, {user_id}, {email}, {date}, {website_id} placeholders." },
];

function resolveUrl(u) {
  if (!u) return "";
  if (u.startsWith("/api/")) return `${API_BASE.replace(/\/api$/, "")}${u}`;
  return u;
}

function AssetUploader({ field, label, accept, currentUrl, onUploaded, testid }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  async function pick(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("field", field);
      form.append("file", file);
      const { data } = await api.post("/admin/site-asset", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  const preview = resolveUrl(currentUrl);
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 p-3 flex items-center gap-3">
      <div className="h-14 w-14 shrink-0 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center overflow-hidden">
        {preview ? (
          <img src={preview} alt={label} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-[10px] font-bold text-slate-400 tracking-widest uppercase">None</span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">{label}</div>
        {currentUrl && (
          <div className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-0.5" title={currentUrl}>
            {currentUrl}
          </div>
        )}
        <label
          data-testid={testid}
          className="mt-1 inline-flex items-center gap-1 cursor-pointer rounded-full bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold px-3 py-1 transition-colors duration-200"
        >
          {uploading ? "Uploading…" : preview ? "Replace" : "Upload"}
          <input ref={fileRef} type="file" accept={accept} onChange={pick} className="hidden" />
        </label>
      </div>
    </div>
  );
}

export default function AdminSettings() {
  const { refresh } = useAuth();
  const [settings, setSettings] = useState(null);
  const [stripeTesting, setStripeTesting] = useState(false);
  const [stripeTestResult, setStripeTestResult] = useState(null);

  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);

  const save = async () => {
    try {
      const { data } = await api.patch("/admin/settings", settings);
      setSettings(data);
      refresh();
      toast.success("Settings saved");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!settings) return <AdminLayout><div className="text-slate-500">Loading…</div></AdminLayout>;

  const setField = (k, v) => setSettings({ ...settings, [k]: v });

  return (
    <AdminLayout>
      <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white mb-5" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>Site Settings</h1>

      {/* SEO & Branding */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">SEO &amp; Branding</div>
        <div className="grid md:grid-cols-2 gap-4">
          {SEO_FIELDS.map((f) => (
            <div key={f.key} className={f.type === "textarea" ? "md:col-span-2" : ""}>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{f.label}</label>
              {f.type === "textarea" ? (
                <textarea
                  data-testid={`settings-${f.key}`}
                  value={settings[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  rows={3}
                  className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
                />
              ) : (
                <input
                  data-testid={`settings-${f.key}`}
                  type="text"
                  value={settings[f.key] ?? ""}
                  onChange={(e) => setField(f.key, e.target.value)}
                  className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
                />
              )}
              {f.hint && <div className="text-[11px] text-slate-500 mt-1">{f.hint}</div>}
            </div>
          ))}

          <AssetUploader
            field="favicon_url"
            label="Favicon"
            accept=".ico,.png,.svg"
            currentUrl={settings.favicon_url}
            onUploaded={(url) => setField("favicon_url", url)}
            testid="settings-favicon-upload"
          />
          <AssetUploader
            field="og_image_url"
            label="Social share image (Open Graph)"
            accept="image/*"
            currentUrl={settings.og_image_url}
            onUploaded={(url) => setField("og_image_url", url)}
            testid="settings-og-upload"
          />
        </div>
      </section>

      {/* Payment processor */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Payment processor</div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Active processor</label>
            <select
              data-testid="settings-payment-processor"
              value={settings.payment_processor ?? "cashapp"}
              onChange={(e) => setField("payment_processor", e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
            >
              <option value="cashapp">Cash App (offline, admin approves)</option>
              <option value="stripe">Stripe (instant, automated)</option>
            </select>
            <div className="text-[11px] text-slate-500 mt-1">
              Stripe requires <b>Enable Stripe</b> to be on and your secret key below.
            </div>
          </div>
          <label className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800 p-3 self-end">
            <input
              data-testid="toggle-stripe_enabled"
              type="checkbox"
              checked={!!settings.stripe_enabled}
              onChange={(e) => setField("stripe_enabled", e.target.checked)}
              className="h-5 w-5"
            />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Enable Stripe checkout</span>
          </label>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Stripe secret key</label>
            <input
              data-testid="settings-stripe_secret_key"
              type="password"
              autoComplete="off"
              placeholder="sk_test_… or sk_live_…"
              value={settings.stripe_secret_key ?? ""}
              onChange={(e) => setField("stripe_secret_key", e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 font-mono text-sm"
            />
            <div className="text-[11px] text-slate-500 mt-1 flex items-center justify-between gap-3 flex-wrap">
              <span>
                Stored securely and never exposed publicly. Get yours from{" "}
                <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noreferrer noopener" className="text-blue-700 dark:text-blue-400 hover:underline">
                  dashboard.stripe.com/apikeys
                </a>.
              </span>
              <button
                type="button"
                data-testid="stripe-test-connection-btn"
                disabled={stripeTesting || !settings.stripe_secret_key}
                onClick={async () => {
                  setStripeTesting(true);
                  setStripeTestResult(null);
                  try {
                    const { data } = await api.post("/admin/stripe/test", {
                      stripe_secret_key: settings.stripe_secret_key,
                    });
                    setStripeTestResult({ ok: true, ...data });
                    toast.success(`Connected as ${data.business_name || data.account_id} · ${data.mode} mode`);
                  } catch (e) {
                    const detail = e.response?.data?.detail || e.message || "Unknown error";
                    setStripeTestResult({ ok: false, error: detail });
                    toast.error(detail);
                  } finally {
                    setStripeTesting(false);
                  }
                }}
                className="rounded-full bg-blue-700 hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed text-white text-xs font-black uppercase tracking-widest px-3 py-1.5 transition-colors duration-200"
              >
                {stripeTesting ? "Testing…" : "Test connection"}
              </button>
            </div>
            {stripeTestResult && (
              <div
                data-testid="stripe-test-result"
                className={`mt-2 rounded-lg px-3 py-2 text-xs font-semibold ${
                  stripeTestResult.ok
                    ? "bg-green-50 dark:bg-green-500/10 text-green-800 dark:text-green-300 border border-green-200 dark:border-green-700"
                    : "bg-red-50 dark:bg-red-500/10 text-red-800 dark:text-red-300 border border-red-200 dark:border-red-700"
                }`}
              >
                {stripeTestResult.ok ? (
                  <>
                    ✓ Connected to <b>{stripeTestResult.business_name || stripeTestResult.account_id}</b>
                    {" · "}<b>{stripeTestResult.mode?.toUpperCase()}</b> mode
                    {stripeTestResult.email ? ` · ${stripeTestResult.email}` : ""}
                  </>
                ) : (
                  <>✗ {stripeTestResult.error}</>
                )}
              </div>
            )}
          </div>
          <div className="md:col-span-2">
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Stripe publishable key</label>
            <input
              data-testid="settings-stripe_publishable_key"
              type="text"
              autoComplete="off"
              placeholder="pk_test_… or pk_live_…"
              value={settings.stripe_publishable_key ?? ""}
              onChange={(e) => setField("stripe_publishable_key", e.target.value)}
              className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 font-mono text-sm"
            />
          </div>
        </div>
      </section>

      {/* Google Sign-In */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Google Sign-In</div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800 p-3 md:col-span-2">
            <input
              data-testid="toggle-google_signin_enabled"
              type="checkbox"
              checked={!!settings.google_signin_enabled}
              onChange={(e) => setField("google_signin_enabled", e.target.checked)}
              className="h-5 w-5"
            />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Show &ldquo;Continue with Google&rdquo; on Login &amp; Register</span>
          </label>
          {GOOGLE_FIELDS.map((f) => (
            <div key={f.key} className="md:col-span-2">
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{f.label}</label>
              <input
                data-testid={`settings-${f.key}`}
                type={f.type}
                autoComplete="off"
                placeholder={f.key === "google_client_id" ? "e.g. 1234567890-abc123.apps.googleusercontent.com" : "GOCSPX-..."}
                value={settings[f.key] ?? ""}
                onChange={(e) => setField(f.key, e.target.value)}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100 font-mono text-sm"
              />
              {f.hint && <div className="text-[11px] text-slate-500 mt-1">{f.hint}</div>}
            </div>
          ))}
          <div className="md:col-span-2 rounded-xl bg-blue-50 dark:bg-blue-950/40 border border-blue-200 dark:border-blue-900 p-3 text-xs text-blue-900 dark:text-blue-200">
            <div className="font-bold mb-1">Setup:</div>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Go to <a className="underline" target="_blank" rel="noreferrer noopener" href="https://console.cloud.google.com/apis/credentials">Google Cloud Console → Credentials</a>.</li>
              <li>Create an OAuth 2.0 Client ID (type <b>Web application</b>).</li>
              <li>Add your site URL to <b>Authorized JavaScript origins</b> (e.g. <code>{typeof window !== "undefined" ? window.location.origin : ""}</code> and <code>https://trafficexchange.app</code>).</li>
              <li>Paste the Client ID + Secret above and hit <b>Save</b>.</li>
            </ol>
          </div>
        </div>
      </section>

      {/* Start Pages */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Start Pages advertising</div>
        <div className="grid md:grid-cols-2 gap-4">
          <label className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800 p-3 md:col-span-2">
            <input
              data-testid="toggle-start_pages_enabled"
              type="checkbox"
              checked={!!settings.start_pages_enabled}
              onChange={(e) => setField("start_pages_enabled", e.target.checked)}
              className="h-5 w-5"
            />
            <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">Enable Start Pages — shown first before the regular surf queue</span>
          </label>
          {START_PAGE_FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{f.label}</label>
              <input
                data-testid={`settings-${f.key}`}
                type="number"
                step={f.step}
                value={settings[f.key] ?? ""}
                onChange={(e) => setField(f.key, Number(e.target.value))}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
              />
            </div>
          ))}
        </div>
      </section>

      {/* Core settings */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-4">Site &amp; economy</div>
        <div className="grid md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <label className="text-xs font-bold uppercase tracking-widest text-slate-500">{f.label}</label>
              <input
                data-testid={`settings-${f.key}`}
                type={f.type}
                step={f.step}
                value={settings[f.key] ?? ""}
                onChange={(e) => setField(f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-2 text-slate-900 dark:text-slate-100"
              />
            </div>
          ))}
          <div className="md:col-span-2 grid md:grid-cols-2 gap-3">
            {TOGGLES.map((t) => (
              <label key={t.key} className="flex items-center gap-3 rounded-xl bg-slate-50 dark:bg-slate-800 p-3">
                <input
                  data-testid={`toggle-${t.key}`}
                  type="checkbox"
                  checked={!!settings[t.key]}
                  onChange={(e) => setField(t.key, e.target.checked)}
                  className="h-5 w-5"
                />
                <span className="text-sm font-semibold text-slate-700 dark:text-slate-200">{t.label}</span>
              </label>
            ))}
          </div>
        </div>
      </section>

      <div className="mt-5">
        <button
          data-testid="settings-save-btn"
          onClick={save}
          className="rounded-full bg-blue-700 hover:bg-blue-800 text-white font-bold px-6 py-3 transition-colors duration-200"
        >
          Save settings
        </button>
      </div>
    </AdminLayout>
  );
}
